import { useState } from "react";
import type { WorkspaceModel } from "./useWorkspace";
import {
  draftingKinds,
  draftingLabels,
  type DraftingKind,
  type DraftingProps,
} from "../shared/drafting";
import {
  draftingStyle,
  resolvedDraftingStyle,
  setDraftingLength,
} from "./drafting-defaults";
import { scaleSource, sourceMeters } from "./source-scale";
export function DraftingControls({ model }: { model: WorkspaceModel }) {
  const { editor, tool, selected } = model;
  const [, refresh] = useState(0);
  const shape =
    tool === "select" &&
    selected.length === 1 &&
    selected[0].type === "drafting"
      ? selected[0]
      : undefined;
  const kind = shape?.props.kind ?? (tool as DraftingKind);
  if (
    !model.canEdit ||
    !draftingKinds.includes(kind) ||
    (shape && editor.isShapeOrAncestorLocked(shape))
  )
    return null;
  const meters = sourceMeters(
    editor,
    shape ? scaleSource(editor, shape) : model.active,
  );
  const value = shape?.props ?? resolvedDraftingStyle(editor, kind, meters);
  const set = (patch: Partial<DraftingProps>) => {
    if (shape) {
      editor.markHistoryStoppingPoint("Drafting properties");
      editor.updateShape({ id: shape.id, type: "drafting", props: patch });
    } else Object.assign(draftingStyle(editor, kind), patch);
    refresh((n) => n + 1);
  };
  const length = (key: "width" | "thickness", label: string) => (
    <label>
      {label}{" "}
      <input
        aria-label={label}
        type="number"
        min={1}
        max={meters ? 100000 : 5000}
        step={meters ? 10 : 1}
        value={Math.round(value[key] * (meters ? meters * 1000 : 1))}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (n > 0 && n <= (meters ? 100000 : 5000)) {
            if (shape)
              set({ [key]: Math.min(5000, n / (meters ? meters * 1000 : 1)) });
            else {
              setDraftingLength(editor, kind, key, n, meters);
              refresh((n) => n + 1);
            }
          }
        }}
      />
      <span>{meters ? "mm" : "canvas units"}</span>
    </label>
  );
  return (
    <div
      className="drafting-controls"
      aria-label={`${draftingLabels[kind]} settings`}
    >
      <label>
        Colour{" "}
        <input
          aria-label="Drafting colour"
          type="color"
          value={value.color}
          onChange={(e) => set({ color: e.target.value })}
        />
      </label>
      <label>
        Stroke{" "}
        <input
          aria-label="Drafting stroke width"
          type="number"
          min={0.5}
          max={12}
          step={0.5}
          value={value.weight}
          onChange={(e) => {
            const weight = Number(e.target.value);
            if (weight >= 0.5 && weight <= 12) set({ weight });
          }}
        />
      </label>
      {kind === "wall" && length("thickness", "Wall thickness")}
      {["door", "window"].includes(kind) && length("width", "Opening width")}
      {kind === "door" && (
        <button
          aria-pressed={value.flip}
          onClick={() => set({ flip: !value.flip })}
        >
          Flip swing
        </button>
      )}
      {kind === "leader" && (
        <label>
          Label{" "}
          <input
            aria-label="Leader label"
            maxLength={160}
            value={value.label}
            onChange={(e) => set({ label: e.target.value })}
          />
        </label>
      )}
    </div>
  );
}
